import { useEffect, useState } from "react";
import { http, money, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function Impostazioni() {
  const [cleaners, setCleaners] = useState([]);
  const [newName, setNewName] = useState("");
  const [tariff, setTariff] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([http.get("/cleaners"), http.get("/laundry/tariff")]);
    setCleaners(c.data); setTariff(t.data);
  };
  useEffect(() => { load(); }, []);

  const addCleaner = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await http.post("/cleaners", { name: newName.trim(), active: true, default_apartment_ids: [] });
      setNewName(""); toast.success("Operatrice aggiunta"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleCleaner = async (c) => {
    await http.put(`/cleaners/${c.id}`, { name: c.name, active: !c.active, default_apartment_ids: c.default_apartment_ids || [], notes: c.notes || null });
    load();
  };
  const removeCleaner = async (c) => {
    if (!window.confirm(`Rimuovere ${c.name}? Se ha storico verrà solo disattivata (nessun dato perso).`)) return;
    const { data } = await http.delete(`/cleaners/${c.id}`);
    toast.success(data.deactivated ? `${c.name} disattivata (${data.cleanings} pulizie nello storico conservate)` : `${c.name} eliminata`);
    load();
  };

  const updItem = (key, patch) => setTariff((t) => ({ ...t, items: t.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));
  const saveTariff = async () => {
    setSaving(true);
    try {
      const payload = {
        vat_percent: Number(tariff.vat_percent) || 0,
        items: tariff.items.map((i) => ({ key: i.key, label: i.label, net_price: Number(i.net_price) || 0, active: !!i.active })),
      };
      const { data } = await http.put("/laundry/tariff", payload);
      setTariff(data); toast.success("Tariffario aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const vat = Number(tariff?.vat_percent) || 0;

  return (
    <div className="space-y-6" data-testid="impostazioni-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--teal-medium)] font-semibold">Configurazione</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Impostazioni</h1>
      </div>

      <Card className="p-5 rounded-2xl" data-testid="cleaners-card">
        <h2 className="font-display text-lg md:text-lg font-bold text-slate-900">Operatrici pulizie</h2>
        <p className="text-xs text-slate-500 mt-1 mb-4">Le operatrici disponibili nei form Pulizie. Chi ha storico non viene eliminata ma disattivata: i vecchi record e le statistiche restano intatti.</p>
        <form onSubmit={addCleaner} className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input placeholder="Nome nuova operatrice" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-cleaner-input" className="sm:max-w-xs" />
          <Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] min-h-[44px] rounded-xl" data-testid="add-cleaner-btn"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
        </form>
        <div className="space-y-2">
          {cleaners.length === 0 && <div className="text-slate-400 text-sm">Nessuna operatrice configurata.</div>}
          {cleaners.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-xl px-3 py-2.5" data-testid={`cleaner-${c.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-semibold text-slate-900 truncate">{c.name}</span>
                {c.active
                  ? <Badge className="bg-[color:var(--teal-light)] text-[var(--teal-deep)]">Attiva</Badge>
                  : <Badge variant="outline" className="border-slate-300 text-slate-500">Disattivata</Badge>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={!!c.active} onCheckedChange={() => toggleCleaner(c)} data-testid={`cleaner-toggle-${c.id}`} />
                <button onClick={() => removeCleaner(c)} data-testid={`cleaner-del-${c.id}`} className="p-2 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {tariff && (
        <Card className="p-5 rounded-2xl" data-testid="tariff-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900">Tariffario lavanderia</h2>
              <p className="text-xs text-slate-500 mt-1">Prezzi <b>netti</b> (IVA esclusa), uguali per tutti gli appartamenti. Il calcolo somma qtà × netto, applica l'IVA sul totale e arrotonda solo alla fine.</p>
            </div>
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">IVA %</Label><Input type="number" step="0.1" className="w-24" value={tariff.vat_percent} onChange={(e) => setTariff({ ...tariff, vat_percent: e.target.value })} data-testid="tariff-vat" /></div>
              <Button onClick={saveTariff} disabled={saving} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] min-h-[44px] rounded-xl" data-testid="tariff-save"><Save className="h-4 w-4 mr-1" /> {saving ? "Salvo..." : "Salva"}</Button>
            </div>
          </div>
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm" data-testid="tariff-table">
              <thead><tr className="text-left border-b border-slate-200 text-[11px] uppercase text-slate-500">
                <th className="py-2 pr-3">Articolo</th><th className="pr-3 text-right">Prezzo netto €</th>
                <th className="pr-3 text-right">IVA {vat}%</th><th className="pr-3 text-right">Lordo €</th><th className="pr-3 text-center">Attivo</th>
              </tr></thead>
              <tbody>
                {tariff.items.map((i) => {
                  const net = Number(i.net_price) || 0;
                  return (
                    <tr key={i.key} className="border-b border-slate-100" data-testid={`tariff-row-${i.key}`}>
                      <td className="py-2.5 pr-3 font-medium">{i.label}</td>
                      <td className="pr-3 text-right"><Input type="number" step="0.01" className="w-24 ml-auto text-right" value={i.net_price} onChange={(e) => updItem(i.key, { net_price: e.target.value })} data-testid={`tariff-net-${i.key}`} /></td>
                      <td className="pr-3 text-right tabular-nums text-slate-500">{money(net * vat / 100)}</td>
                      <td className="pr-3 text-right tabular-nums font-semibold">{money(net * (1 + vat / 100))}</td>
                      <td className="pr-3 text-center"><Switch checked={!!i.active} onCheckedChange={(v) => updItem(i.key, { active: v })} data-testid={`tariff-active-${i.key}`} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
